/**
 * Module: `search/highlighting.ts` — client library utilities.
 * @remarks See exports; keep imports from concrete files when possible.
 */

// NOTE: Ce module dépend volontairement de React au runtime pour produire des
// ReactNodes (via React.createElement) utilisés dans le rendu. Il n'exporte
// ni composant ni hook, mais reste couplé à React au niveau du renderer.
import React from "react";

import { computeFuseHighlightIndices } from "@/lib/search/fuzzy";
import { searchQueryTerms } from "@/lib/search/query-terms";
import type { MatchIndices } from "@/types";

/**
 * Surligne via les indices Fuse (même config / requête que la recherche liste).
 *
 * `matchIndices` : repli si la requête n’a aucun token ≥ 2 car.
 * (ex. tests unitaires avec indices explicites).
 */
export function highlightText(
  text: string,
  searchQuery: string,
  matchIndices?: MatchIndices
): React.ReactNode {
  if (!searchQuery?.trim()) {
    return text;
  }

  const terms = searchQueryTerms(searchQuery);
  if (terms.length > 0) {
    const idx = computeFuseHighlightIndices(text, searchQuery);
    if (idx.length > 0) {
      return highlightWithIndices(text, idx);
    }
    return text;
  }

  if (matchIndices && matchIndices.length > 0) {
    return highlightWithIndices(text, matchIndices);
  }

  return text;
}

function highlightWithIndices(
  text: string,
  indices: MatchIndices
): React.ReactNode {
  if (indices.length === 0) {
    return text;
  }

  const sortedIndices = [...indices].sort((a, b) => a[0] - b[0]);

  const mergedRanges: [number, number][] = [];
  for (const [start, end] of sortedIndices) {
    if (mergedRanges.length === 0) {
      mergedRanges.push([start, end]);
    } else {
      const lastRange = mergedRanges[mergedRanges.length - 1];
      if (start <= lastRange[1] + 1) {
        lastRange[1] = Math.max(lastRange[1], end);
      } else {
        mergedRanges.push([start, end]);
      }
    }
  }

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  mergedRanges.forEach(([start, end], idx) => {
    if (start > lastIndex) {
      parts.push(text.slice(lastIndex, start));
    }

    parts.push(
      React.createElement(
        "mark",
        {
          key: `match-${idx}`,
          className:
            "bg-secondary/30 dark:bg-secondary/35 text-inherit rounded-sm px-0.5",
        },
        text.slice(start, end + 1)
      )
    );

    lastIndex = end + 1;
  });

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return React.createElement(React.Fragment, null, ...parts);
}
